<main class="fixed inset-0 flex flex-row overflow-hidden font-sans">

  <!-- Left Panel: Brand Identity -->
  <aside class="hidden lg:flex lg:w-[44%] flex-col justify-between flex-shrink-0 relative overflow-hidden bg-primary-800">

    <!-- Subtle dot grid -->
    <div class="absolute inset-0"
      style="background-image: radial-gradient(circle, rgba(255,255,255,0.07) 1px, transparent 1px); background-size: 28px 28px;"></div>
    <div class="absolute bottom-0 right-0 w-[500px] h-[500px] -mb-32 -mr-32 rounded-full"
      style="background: radial-gradient(circle, rgba(255,255,255,0.06) 0%, transparent 65%);"></div>

    <div class="relative z-10 flex flex-col h-full px-14 py-12">

      <!-- Logo -->
      <div class="flex items-center gap-3">
        <div class="w-9 h-9 rounded-lg flex items-center justify-center bg-white dark:bg-slate-800/10 border border-white/15">
          <img [src]="_images.appLogo" alt="Logo" class="w-5 h-5 brightness-0 invert" />
        </div>
        <div>
          <p class="text-[13px] font-bold text-white leading-none">Direct Savings & Loans</p>
          <p class="text-[9px] font-semibold uppercase tracking-[0.22em] text-white/35 mt-1">Back Office</p>
        </div>
      </div>

      <!-- Main copy -->
      <div class="flex-1 flex flex-col justify-center">
        <p class="text-[10px] font-bold uppercase tracking-[0.18em] text-white/35 mb-5">Access Recovery</p>
        <h2 class="text-[2.6rem] font-bold text-white leading-[1.15] tracking-tight mb-5">
          Restore your<br/>secure access<br/>in minutes.
        </h2>
        <p class="text-[13px] text-white/45 leading-relaxed max-w-[300px]">
          Follow the verification steps to securely restore your administrative credentials.
        </p>

        <div class="w-10 h-0.5 bg-white dark:bg-slate-800/20 my-8 rounded-full"></div>

        <!-- Steps -->
        <div class="space-y-4">
          @for (step of ['Identity Verification', 'OTP Code Validation', 'Security Key Reset']; track $index) {
          <div class="flex items-center gap-3">
            <div class="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-bold text-white bg-white dark:bg-slate-800/15 border border-white/20">{{ $index + 1 }}</div>
            <span class="text-[12px] text-white/55">{{ step }}</span>
          </div>
          }
        </div>
      </div>

      <!-- Footer -->
      <div class="flex items-center justify-between pt-5 border-t border-white/10">
        <span class="text-[9px] font-semibold uppercase tracking-widest text-white/25">© {{ currentYear }} DSL</span>
      </div>

    </div>
  </aside>

  <!-- Right Panel: Forms -->
  <section class="flex-1 flex items-center justify-center bg-slate-50 dark:bg-slate-950 p-8 overflow-y-auto">
    <div class="w-full max-w-sm">

      <!-- Step 1: Email Entry -->
      @if (currentStep === 1) {
      <div>
        <div class="mb-8">
          <h1 class="text-2xl font-bold tracking-tight text-primary-800 dark:text-primary-400">Recover Access</h1>
          <p class="text-xs text-slate-500 dark:text-slate-400 mt-1.5 leading-relaxed">Enter your registered email to receive a verification code.</p>
        </div>

        <form [formGroup]="emailForm" (ngSubmit)="submitEmail()" class="space-y-5">
          <div>
            <app-primary-input [type]="'text'" [placeholder]="'admin@directsl.com'" [label]="'Official Email'"
              [showLabel]="true" [image]="_images.personIcon" formControlName="email"></app-primary-input>
            @if (emailForm.get('email')?.invalid && emailForm.get('email')?.touched) {
            <p class="text-error text-[11px] font-medium mt-1.5 pl-1">Enter a valid email address.</p>
            }
          </div>

          <div class="pt-2 space-y-3">
            <button type="submit" [disabled]="isSendingCode"
              class="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-bold text-white rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed bg-primary-800 hover:bg-primary-900">
              @if (isSendingCode) {
              <svg class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
              </svg>
              } @else {
              Send Verification Code
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
              }
            </button>
            <button type="button" (click)="goToLogin()"
              class="w-full text-xs font-bold text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:text-slate-300 transition-colors uppercase tracking-widest py-2">
              Back to Login
            </button>
          </div>
        </form>
      </div>
      }

      <!-- Step 2: Code + New Password -->
      @if (currentStep === 2) {
      <div>
        <div class="mb-8">
          <h1 class="text-2xl font-bold tracking-tight text-primary-800 dark:text-primary-400">Reset Password</h1>
          <p class="text-xs text-slate-500 dark:text-slate-400 mt-1.5 leading-relaxed">Enter the code sent to your email and set a new password.</p>
        </div>

        <form [formGroup]="resetPasswordForm" (ngSubmit)="resetPassword()" class="space-y-5">
          <div>
            <app-primary-input [type]="'text'" [placeholder]="'6-digit code'" [label]="'Verification Code'"
              [showLabel]="true" [image]="_images.personIcon" formControlName="code"></app-primary-input>
            @if (resetPasswordForm.get('code')?.invalid && resetPasswordForm.get('code')?.touched) {
            <p class="text-error text-[11px] font-medium mt-1.5 pl-1">Verification code is required.</p>
            }
          </div>

          <div>
            <app-primary-input [type]="showPassword ? 'text' : 'password'" [placeholder]="'New password'"
              [label]="'New Password'" [showLabel]="true" [image]="!showPassword ? _images.unvisible : _images.visible"
              formControlName="newPassword" (onViewEvent)="togglePasswordVisibility()"></app-primary-input>
            @if (resetPasswordForm.get('newPassword')?.invalid && resetPasswordForm.get('newPassword')?.touched) {
            <p class="text-error text-[11px] font-medium mt-1.5 pl-1">Minimum 6 characters required.</p>
            }
          </div>

          <div>
            <app-primary-input [type]="showConfirmPassword ? 'text' : 'password'" [placeholder]="'Confirm password'"
              [label]="'Confirm Password'" [showLabel]="true"
              [image]="!showConfirmPassword ? _images.unvisible : _images.visible" formControlName="confirmPassword"
              (onViewEvent)="toggleConfirmPasswordVisibility()"></app-primary-input>
            @if (resetPasswordForm.get('confirmPassword')?.invalid && resetPasswordForm.get('confirmPassword')?.touched) {
            <p class="text-error text-[11px] font-medium mt-1.5 pl-1">Passwords do not match.</p>
            }
          </div>

          <div class="pt-2 space-y-3">
            <button type="submit" [disabled]="isResettingPassword"
              class="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-bold text-white rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed bg-primary-800 hover:bg-primary-900">
              @if (isResettingPassword) {
              <svg class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
              </svg>
              } @else {
              Restore Access
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
              }
            </button>
            <button type="button" (click)="goBackToEmailStep()"
              class="w-full text-xs font-bold text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:text-slate-300 transition-colors uppercase tracking-widest py-2">
              Resend Code
            </button>
          </div>
        </form>
      </div>
      }

      <div class="mt-10 pt-8 border-t border-slate-100 dark:border-slate-700/50">
        <p class="text-xs text-slate-400">
          Persistent access issues? Contact
          <a href="mailto:support@directsl.com" class="font-bold hover:underline text-primary-800 dark:text-primary-400 hover:text-primary-900">IT Support</a>
        </p>
      </div>

    </div>
  </section>

</main>
